import { keepPreviousData, useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  chatbotV2Service,
  type SupportConversation,
} from "../services/chatbotV2/chatbotV2Service";

export type ConversationBucketFilter = "open" | "resolved";
export type HumanHelpFilter = "all" | "with" | "without";

export type SupportConversationFilters = {
  buckets: ConversationBucketFilter[];
  search: string;
  priority: string;
  customerState: string;
  labels: string[];
  humanHelp: HumanHelpFilter;
  channel: string;
  unanswered: boolean;
  pendingApproval: boolean;
  deliveryError: boolean;
};

const timestampMs = (value?: string | null) => {
  if (!value) return 0;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const mergeConversationRecord = (base: SupportConversation, updated: SupportConversation): SupportConversation => ({
  ...base,
  ...updated,
  contact: updated.contact ?? base.contact,
  labels: updated.labels ?? base.labels,
  metadata_json: {
    ...(base.metadata_json || {}),
    ...(updated.metadata_json || {}),
  },
});

const sortConversationsByUpdatedAt = (rows: SupportConversation[]) =>
  [...rows].sort((a, b) => timestampMs(b.updated_at) - timestampMs(a.updated_at));

const mergeConversationLists = (lists: SupportConversation[][], limit = 1000) => {
  const byId = new Map<number, SupportConversation>();
  lists.flat().forEach((conversation) => {
    const current = byId.get(conversation.id);
    byId.set(conversation.id, current ? mergeConversationRecord(current, conversation) : conversation);
  });
  return sortConversationsByUpdatedAt(Array.from(byId.values())).slice(0, limit);
};

const filtersQueryKey = (filters: SupportConversationFilters, debouncedSearch: string) => ({
  buckets: filters.buckets.join(","),
  search: debouncedSearch.trim(),
  priority: filters.priority,
  customerState: filters.customerState,
  labels: filters.labels.join(","),
  humanHelp: filters.humanHelp,
  channel: filters.channel,
  unanswered: filters.unanswered,
  pendingApproval: filters.pendingApproval,
  deliveryError: filters.deliveryError,
});

const toPagedParams = (
  bucket: ConversationBucketFilter,
  filters: SupportConversationFilters,
  debouncedSearch: string,
  cursor?: string | null
) => ({
  bucket,
  search: debouncedSearch.trim() || undefined,
  priority: filters.priority || undefined,
  customer_state: filters.customerState || undefined,
  labels: filters.labels.length ? filters.labels.join(",") : undefined,
  human_help: filters.humanHelp === "all" ? undefined : filters.humanHelp,
  channel: filters.channel || undefined,
  unanswered: filters.unanswered ? true : undefined,
  pending_approval: filters.pendingApproval ? true : undefined,
  delivery_error: filters.deliveryError ? true : undefined,
  cursor: cursor ?? undefined,
  limit: 50,
});

const itemsFromInfiniteData = (data?: { pages: Array<{ items: SupportConversation[] }> }) =>
  (data?.pages ?? []).flatMap((page) => page.items);

export function useSupportConversationsInfinite(filters: SupportConversationFilters) {
  const queryClient = useQueryClient();
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(filters.search), 300);
    return () => window.clearTimeout(timer);
  }, [filters.search]);

  const buckets = filters.buckets.length ? filters.buckets : (["open"] as ConversationBucketFilter[]);
  const queryKeyBase = filtersQueryKey(filters, debouncedSearch);
  const includeOpen = buckets.includes("open");
  const includeResolved = buckets.includes("resolved");

  const openQuery = useInfiniteQuery({
    queryKey: ["support-conversations", "open", queryKeyBase],
    queryFn: ({ pageParam }) =>
      chatbotV2Service.listConversationsPaged(
        toPagedParams("open", filters, debouncedSearch, pageParam)
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    placeholderData: keepPreviousData,
    enabled: includeOpen,
  });

  const resolvedQuery = useInfiniteQuery({
    queryKey: ["support-conversations", "resolved", queryKeyBase],
    queryFn: ({ pageParam }) =>
      chatbotV2Service.listConversationsPaged(
        toPagedParams("resolved", filters, debouncedSearch, pageParam)
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    placeholderData: keepPreviousData,
    enabled: includeResolved,
  });

  const conversations = useMemo(() => {
    const lists: SupportConversation[][] = [];
    if (includeOpen) lists.push(itemsFromInfiniteData(openQuery.data));
    if (includeResolved) lists.push(itemsFromInfiniteData(resolvedQuery.data));
    return mergeConversationLists(lists, 1000);
  }, [includeOpen, includeResolved, openQuery.data, resolvedQuery.data]);

  const isLoading =
    (includeOpen && openQuery.isLoading && !openQuery.data) ||
    (includeResolved && resolvedQuery.isLoading && !resolvedQuery.data);
  const isFetching =
    (includeOpen && openQuery.isFetching) || (includeResolved && resolvedQuery.isFetching);
  const hasNextPage =
    (includeOpen && openQuery.hasNextPage) || (includeResolved && resolvedQuery.hasNextPage);
  const isFetchingNextPage =
    (includeOpen && openQuery.isFetchingNextPage) || (includeResolved && resolvedQuery.isFetchingNextPage);

  const fetchNextPage = useCallback(() => {
    if (includeOpen && openQuery.hasNextPage && !openQuery.isFetchingNextPage) {
      void openQuery.fetchNextPage();
    }
    if (includeResolved && resolvedQuery.hasNextPage && !resolvedQuery.isFetchingNextPage) {
      void resolvedQuery.fetchNextPage();
    }
  }, [includeOpen, includeResolved, openQuery, resolvedQuery]);

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["support-conversations"] });
  }, [queryClient]);

  const patchConversation = useCallback(
    (updated: SupportConversation) => {
      queryClient.setQueriesData<{
        pages: Array<{ items: SupportConversation[]; next_cursor?: string | null; has_more?: boolean }>;
        pageParams: unknown[];
      }>({ queryKey: ["support-conversations"] }, (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            items: page.items.map((row) =>
              row.id === updated.id ? mergeConversationRecord(row, updated) : row
            ),
          })),
        };
      });
    },
    [queryClient]
  );

  const refreshOverview = useCallback(async () => {
    const [overviewResult, healthResult] = await Promise.all([
      chatbotV2Service.getOverview(),
      chatbotV2Service.getOperationsHealth(),
    ]);
    return { overviewResult, healthResult };
  }, []);

  const isSearchPending = filters.search !== debouncedSearch;

  return {
    conversations,
    isLoading,
    isFetching,
    isSearchPending,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    invalidate,
    patchConversation,
    refreshOverview,
  };
}
